import { TaskScheduler } from "../../../../core/task-scheduler";
import { Patterns } from "../../../../constants/patterns";
import { CardPreviewMediaAreaRender } from "./card-preview-media-area-render";
import { Roles } from "../../../../constants/roles";
import type { DetectedLinksMap } from "../../../core/sns-detector";
import { snsPreviewManagerForm } from "../../sns-preview-manager-form";

export class YouTubeCardPreviewRender {
    public static addYouTubePreviewToCard(cardElement: Element, detectedLinks: DetectedLinksMap): void {
        if (!detectedLinks.youtube?.length) return;

        const boardMediaArea = CardPreviewMediaAreaRender.findCardMediaArea(cardElement);
        if (!boardMediaArea) return;

        const existingPreview = snsPreviewManagerForm.getExistingCardPreview(cardElement, Roles.YOUTUBE_PREVIEW);
        if (existingPreview) return;

        const youtubeUrl = String(detectedLinks.youtube[0]?.url || "");
        const youtubePreview = this.generateLegacyYouTubePreview(youtubeUrl);
        if (!youtubePreview) return;

        const placeholder = snsPreviewManagerForm.getBoardMediaPlaceholder(boardMediaArea);
        if (placeholder) placeholder.remove();

        boardMediaArea.appendChild(youtubePreview);
    }

    public static generateLegacyYouTubePreview(youtubeUrl: string): HTMLElement | null {
        if (!youtubeUrl || typeof youtubeUrl !== "string") {
            console.warn("generateLegacyYouTubePreview: Invalid input", typeof youtubeUrl, youtubeUrl);
            return null;
        }

        const videoIdMatch = youtubeUrl.match(Patterns.YOUTUBE_ID);
        if (!videoIdMatch) return null;

        const videoId = videoIdMatch[1];
        const previewContainer = document.createElement("div");
        previewContainer.className = "youtube-preview-container";
        previewContainer.dataset.role = Roles.YOUTUBE_PREVIEW;
        previewContainer.dataset.youtubeVideoId = videoId;
        previewContainer.style.cssText = `
                position: relative;
                width: 80px;
                height: 80px;
                background: #f0f0f0;
                border-radius: 8px;
                overflow: hidden;
                border: 1px solid #ccc;
            `;

        const placeholder = document.createElement("div");
        placeholder.className = "youtube-thumbnail-placeholder";
        placeholder.style.cssText = `
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: #f0f0f0;
                display: flex;
                align-items: center;
                justify-content: center;
            `;

        const spinnerDiv = document.createElement("div");
        spinnerDiv.className = "spinner spinner-small";
        placeholder.appendChild(spinnerDiv);

        const thumbnail = document.createElement("img");
        thumbnail.style.cssText = `
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                object-fit: cover;
                opacity: 0;
                transition: opacity 0.3s ease-in-out;
            `;
        thumbnail.alt = "YouTube Thumbnail";

        const playButton = document.createElement("div");
        playButton.innerHTML = "▶";
        playButton.dataset.role = Roles.YOUTUBE_PLAY_BUTTON;
        playButton.dataset.youtubeVideoId = videoId;
        playButton.style.cssText = `
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: white;
                font-size: 16px;
                text-shadow: 0 0 3px rgba(0,0,0,0.8);
                pointer-events: none;
                opacity: 0;
                transition: opacity 0.3s ease-in-out;
                z-index: 2;
            `;

        previewContainer.appendChild(placeholder);
        previewContainer.appendChild(thumbnail);
        previewContainer.appendChild(playButton);

        const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
        const loadThumbnail = (): void => {
            const img = new Image();
            img.onload = () => {
                thumbnail.src = img.src;
                thumbnail.style.opacity = "1";
                playButton.style.opacity = "1";
                placeholder.style.opacity = "0";
                TaskScheduler.schedule(() => {
                    if (placeholder.parentNode) placeholder.remove();
                }, "raf");
            };
            img.onerror = () => {
                placeholder.innerHTML = "📺";
                placeholder.style.background = "#ffebee";
                placeholder.style.color = "#c62828";
                placeholder.style.animation = "none";
            };
            TaskScheduler.schedule(() => {
                img.src = thumbnailUrl;
            }, "raf");
        };

        requestAnimationFrame(loadThumbnail);
        return previewContainer;
    }
}
